import { createFileRoute, redirect } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Scale } from 'lucide-react';
import { authClient } from '@/lib/auth-client';
import { api } from '@/lib/api-client';
import {
  useOnboardingStatus,
  useUserProfile,
  useCurrentWorkspace,
  useUpdateProfile,
  useUpdateWorkspace,
  useSendInvitation,
} from '@/hooks/api/use-onboarding';
import { WizardLayout } from '@/components/onboarding/wizard-layout';
import { StepName } from '@/components/onboarding/step-name';
import { StepWorkspace } from '@/components/onboarding/step-workspace';
import { StepInvitations } from '@/components/onboarding/step-invitations';
import { SuccessScreen } from '@/components/onboarding/success-screen';

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) {
      throw redirect({ to: '/login' });
    }

    // Check if user is deactivated — prevent re-activating workspace
    if (!session.data.session?.activeOrganizationId) {
      try {
        const result = await api.get<{
          data: { deactivated?: boolean };
        }>('/users/me/onboarding');
        if (result.data?.deactivated) {
          throw redirect({ to: '/deactivated' });
        }
      } catch (e) {
        if (e && typeof e === 'object' && 'to' in e) throw e;
      }
    }

    // Ensure active organization is set.
    // Register creates org + setActive, but session cookie may be stale.
    if (!session.data.session?.activeOrganizationId) {
      try {
        const orgs = await authClient.organization.list();
        if (orgs.data && orgs.data.length > 0) {
          await authClient.organization.setActive({ organizationId: orgs.data[0].id });
        } else {
          // Edge case: no org exists (e.g. direct URL access). Create one.
          const email = session.data.user?.email ?? 'workspace';
          const slug = email
            .split('@')[0]
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '-')
            .slice(0, 30);
          const orgResult = await authClient.organization.create({ name: slug, slug });
          if (orgResult.data) {
            await authClient.organization.setActive({ organizationId: orgResult.data.id });
          }
        }
      } catch {
        // If org setup fails, continue — requests may fail with 403 but won't crash
      }
    }

    // If onboarding is already complete, skip straight to dashboard.
    // Prevents showing the success screen on every login.
    try {
      const result = await api.get<{
        data: { profileCompleted: boolean; workspaceCompleted: boolean; deactivated?: boolean };
      }>('/users/me/onboarding');
      if (result.data?.deactivated) {
        throw redirect({ to: '/deactivated' });
      }
      if (result.data?.profileCompleted && result.data?.workspaceCompleted) {
        throw redirect({ to: '/dashboard' });
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'to' in e) throw e;
      // If check fails, continue to onboarding wizard
    }

    return { session: session.data };
  },
  component: OnboardingPage,
});

const TOTAL_STEPS = 3;

function OnboardingPage() {
  const { t } = useTranslation();
  const [step, setStep] = React.useState<number | null>(null);
  const [direction, setDirection] = React.useState<'forward' | 'backward'>('forward');
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  const { data: onboarding, isLoading: onboardingLoading } = useOnboardingStatus();
  const { data: user, isLoading: userLoading } = useUserProfile();
  const { data: workspace, isLoading: workspaceLoading } = useCurrentWorkspace();

  const updateProfile = useUpdateProfile();
  const updateWorkspace = useUpdateWorkspace();
  const sendInvitation = useSendInvitation();

  // Initialize step from onboarding status
  React.useEffect(() => {
    if (onboarding && step === null) {
      if (onboarding.profileCompleted && onboarding.workspaceCompleted) {
        // All required steps done — show success or redirect
        setShowSuccess(true);
      } else {
        const initialStep = onboarding.currentStep;
        setStep(initialStep);
        // Set initial history state so browser back from step 2 → step 1 works
        window.history.replaceState({ step: initialStep }, '', '/onboarding');
      }
    }
  }, [onboarding, step]);

  // Browser back/forward support
  React.useEffect(() => {
    if (step === null) return;

    const handlePopState = (e: PopStateEvent) => {
      const historyStep = e.state?.step;
      if (typeof historyStep === 'number' && historyStep >= 1 && historyStep <= TOTAL_STEPS) {
        setDirection(historyStep < (step ?? 1) ? 'backward' : 'forward');
        setStep(historyStep);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [step]);

  const goToStep = React.useCallback((newStep: number, dir: 'forward' | 'backward') => {
    setDirection(dir);
    setStep(newStep);
    setServerError(null);
    window.history.pushState({ step: newStep }, '', '/onboarding');
  }, []);

  // Step 1: Save name
  const handleNameNext = React.useCallback(
    async (name: string) => {
      setServerError(null);
      try {
        await updateProfile.mutateAsync({ name });
        goToStep(2, 'forward');
      } catch {
        setServerError(t('onboarding.errorGeneric'));
      }
    },
    [updateProfile, goToStep, t],
  );

  // Step 2: Save workspace name
  const handleWorkspaceNext = React.useCallback(
    async (name: string) => {
      setServerError(null);
      try {
        await updateWorkspace.mutateAsync({ name });
        goToStep(3, 'forward');
      } catch {
        setServerError(t('onboarding.errorGeneric'));
      }
    },
    [updateWorkspace, goToStep, t],
  );

  // Step 3: Send invitations
  const handleInvitationsNext = React.useCallback(
    async (invitations: { email: string; role: string }[]) => {
      setServerError(null);
      // Send invitations one by one, collect results
      const results = await Promise.allSettled(
        invitations.map((inv) => sendInvitation.mutateAsync(inv)),
      );

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0 && failures.length === results.length) {
        // All failed
        setServerError(t('onboarding.errorGeneric'));
        return;
      }

      // At least some succeeded — proceed to success
      setShowSuccess(true);
    },
    [sendInvitation, t],
  );

  // Step 3: Skip
  const handleSkip = React.useCallback(() => {
    setShowSuccess(true);
  }, []);

  // Loading state
  const isLoading = onboardingLoading || userLoading || workspaceLoading;

  if (isLoading || step === null) {
    if (showSuccess) {
      return <SuccessScreen workspaceName={workspace?.name ?? ''} />;
    }
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-6">
        <div className="flex flex-col items-center gap-4">
          <div className="flex size-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Scale className="size-5" />
          </div>
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </main>
    );
  }

  if (showSuccess) {
    return <SuccessScreen workspaceName={workspace?.name ?? ''} />;
  }

  // Determine pre-fill values
  const userName = user?.name ?? '';
  const workspaceName = workspace?.name !== workspace?.slug ? (workspace?.name ?? '') : '';

  return (
    <>
      <WizardLayout currentStep={step} totalSteps={TOTAL_STEPS} direction={direction}>
        {step === 1 && <StepName defaultName={userName} onNext={handleNameNext} />}

        {step === 2 && (
          <StepWorkspace
            defaultName={workspaceName}
            onNext={handleWorkspaceNext}
            onBack={() => goToStep(1, 'backward')}
          />
        )}

        {step === 3 && (
          <StepInvitations
            onNext={handleInvitationsNext}
            onSkip={handleSkip}
            onBack={() => goToStep(2, 'backward')}
          />
        )}

        {serverError && (
          <div
            className="mt-4 rounded-md border border-destructive/50 bg-destructive/10 p-3"
            role="alert"
            aria-live="assertive"
          >
            <p className="text-sm text-destructive">{serverError}</p>
          </div>
        )}
      </WizardLayout>
    </>
  );
}
